<template>
  <div class="widget-body">
    <v-alert show tabindex="0" v-if="!ric" variant="light" align="center">{{ i18n('messages.noRic') }}</v-alert>
    <c-loader v-else-if="isLoading" />
    <b-table
      v-else-if="items.length"
      dark
      bordered
      :fields="fields"
      hover
      :items="items"
      :sort-compare="sortCompare"
      no-sort-reset
      small
      sticky-header
      striped
      thead-class="text-center"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      @head-clicked="onHeaderClicked"
      @sort-changed="$emit('sortChange', $event)"
      @keydown.native="onKeyDown"
      @click.native="onClick"
      :aria-label="i18n('widget-title')"
    >
      <template #head(_DATE_START)
        ><span>{{ i18n('fields.date') }}</span></template
      >
      <template #head(OPEN_PRC)
        ><span>{{ i18n('fields.open') }}</span></template
      >
      <template #head(HIGH_1)
        ><span>{{ i18n('fields.high') }}</span></template
      >
      <template #head(LOW_1)
        ><span>{{ i18n('fields.low') }}</span></template
      >
      <template #head(CLOSE_PRC)
        ><span>{{ i18n('fields.close') }}</span></template
      >
      <template #head(ACVOL_1)
        ><span>{{ i18n('fields.volume') }}</span></template
      >

      <template #cell(_DATE_START)="data">
        <v-fidlet tabindex="0" :data="data.value || ''" type="date.localed" />
      </template>
      <template #cell(OPEN_PRC)="data">
        <v-fidlet tabindex="0" :data="data.value || ''" :type="formats[data.field.key]" />
      </template>
      <template #cell(HIGH_1)="data">
        <v-fidlet tabindex="0" :data="data.value || ''" :type="formats[data.field.key]" />
      </template>
      <template #cell(LOW_1)="data">
        <v-fidlet tabindex="0" :data="data.value || ''" :type="formats[data.field.key]" />
      </template>
      <template #cell(CLOSE_PRC)="data">
        <v-fidlet tabindex="0" :data="data.value || ''" type="float.price" />
      </template>
      <template #cell(ACVOL_1)="data">
        <v-fidlet tabindex="0" :data="data.value || ''" :type="formats[data.field.key]" />
      </template>
    </b-table>
    <v-alert show tabindex="0" v-else-if="isRequested" variant="light" align="center">
      {{ i18n('messages.nodata') }}
    </v-alert>
    <v-alert show v-else-if="isError" variant="red" align="center">
      {{ i18n('messages.error') }}
    </v-alert>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';

import { API } from '@/state/api';
import moment from 'moment';

export default {
  name: 'w-trade-history-widget',
  components: {
    CLoader
  },
  props: {
    wid: {
      type: String,
      required: true
    },
    ric: {
      type: String,
      required: true
    },
    controlDateFrom: {
      type: Date,
      required: true
    },
    controlDateTo: {
      type: Date,
      required: true
    },
    controlInterval: {
      type: String,
      required: true
    },
    sortBy: {
      type: String,
      required: true
    },
    sortDesc: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      fields: [
        { key: '_DATE_START', sortable: true },
        { key: 'OPEN_PRC', sortable: true },
        { key: 'HIGH_1', sortable: true },
        { key: 'LOW_1', sortable: true },
        { key: 'CLOSE_PRC', sortable: true },
        { key: 'ACVOL_1', sortable: true }
      ],
      formats: {},
      isError: false,
      isLoading: false,
      isRequested: false,
      items: []
    };
  },
  watch: {
    updateHash: {
      immediate: true,
      handler() {
        this.fetch();
      }
    }
  },
  computed: {
    updateHash() {
      return [
        this.ric,
        moment(this.controlDateFrom).format('YYMMDD'),
        moment(this.controlDateTo).format('YYMMDD'),
        this.controlInterval
      ].join();
    }
  },
  methods: {
    sortCompare(aRow, bRow, key, sortDesc) {
      const a = aRow[key];
      const b = bRow[key];

      // if a is NaN, move down
      if (typeof a === 'number' && isNaN(a)) {
        return sortDesc ? -1 : 1;
      }
      // if b is NaN, move down
      if (typeof b === 'number' && isNaN(b)) {
        return sortDesc ? 1 : -1;
      }

      // forward to default comparator
      return null;
    },
    i18n(subkey, ns) {
      return this.$t(`white-label.${ns || 'widgets.tradeHistory'}.${subkey}`);
    },
    nsify(data) {
      return data.map(val => {
        if (typeof val === 'string') return 'q.' + val;
        return Object.keys(val).reduce((acc, fid) => {
          acc[fid.replace('q.', '')] = val[fid];
          return acc;
        }, {});
      });
    },
    onHeaderClicked() {
      return false;
    },
    onKeyDown(event) {
      const isHeaderCell = document.activeElement.tagName === 'TH';

      const currentCell = isHeaderCell ? document.activeElement : document.activeElement.parentElement;

      const currentRow = currentCell.closest('tr');
      const currentPosition = currentCell.getAttribute('aria-colindex');

      let nextCell = null;
      let nextRow = null;

      switch (event.key) {
        case 'Tab':
          if (isHeaderCell) {
            if (currentCell.nextElementSibling === null) event.preventDefault;
            return;
          }
          break;
        case 'ArrowRight':
          nextCell = currentCell.nextElementSibling;
          break;
        case 'ArrowLeft':
          nextCell = currentCell.previousElementSibling;
          break;
        case 'ArrowDown':
          nextRow = isHeaderCell
            ? currentRow.parentElement.nextElementSibling.querySelector('tr')
            : currentRow.nextElementSibling;
          if (nextRow) nextCell = nextRow.querySelector(`td[aria-colindex="${currentPosition}"]`);
          break;
        case 'ArrowUp':
          if (isHeaderCell) break;
          nextRow = currentRow.previousElementSibling;
          if (nextRow) nextCell = nextRow.querySelector(`td[aria-colindex="${currentPosition}"]`);
          else {
            nextCell = currentRow.parentElement.previousElementSibling.querySelector(
              `tr th[aria-colindex="${currentPosition}"]`
            );
          }
          break;
        default:
          return;
      }

      if (nextCell) {
        if (nextCell.tagName === 'TH') nextCell.focus();
        else nextCell.firstChild.focus();
        nextCell.scrollIntoView({ behavior: 'instant', block: 'center' });
      }

      event.preventDefault();
    },
    onClick(event) {
      event.target.focus();
    },
    fetch() {
      if (!this.ric) {
        return;
      }
      this.isLoading = true;
      API.Rest.get(
        '/timeseries/historical' +
          '?ric=' +
          this.ric +
          '&fromDate=' +
          moment(this.controlDateFrom).format('YYYY-MM-DD') +
          '&toDate=' +
          moment(this.controlDateTo).format('YYYY-MM-DD') +
          '&fids=' +
          this.nsify(this.fields.map(f => f.key)) +
          '&samples=' +
          this.controlInterval
      )
        .then(response => {
          if (this.ricVal === 'error') {
            this.isError = true;
            this.items = [];
          } else {
            this.isError = false;
            this.formats = {};

            for (let key in response.data.metadata) {
              this.formats[key.split('.')[1]] = response.data.metadata[key].type;
            }
            this.items = this.nsify(response.data.data, true).map(item => {
              return {
                ...item,
                ACVOL_1: parseInt(item.ACVOL_1),
                HIGH_1: parseFloat(item.HIGH_1),
                LOW_1: parseFloat(item.LOW_1),
                OPEN_PRC: parseFloat(item.OPEN_PRC),
                CLOSE_PRC: parseFloat(item.CLOSE_PRC)
              };
            });
          }
          this.isRequested = true;
          this.isLoading = false;
        })
        .catch(err => {
          console.error(err);
          this.items = [];
          this.isError = true;
          this.isLoading = false;
        });
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/widgets/trade-history-widget';
</style>
